import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Logo from '../Logo';
import { ContactButton } from '../Contact';
import mediaQueries from '../../styles/mediaQueries';

const FooterWrapper = styled.footer`
  width: 100%;
  font-family: var(--font-primary);
  padding: 0 20px;
`;

const FooterContainer = styled.div`
  background-color: #f5f5f5;
  padding: 60px 20px;
  text-align: center;
  border-radius: 42px 42px 0 0;
  max-width: 1000px;
  margin: 0 auto;
`;

const Heading = styled.h2`
  font-size: 48px;
  color: ${({theme}) => theme.colors.secondary};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-weight: var(--font-weight-semibold);

    ${mediaQueries.large} {
  }
  
  ${mediaQueries.medium} {
    font-size: 34px;
  }
  
  ${mediaQueries.small} {
  }
`;

const Subheading = styled.p`
  font-size: 20px;
  color: ${({theme}) => theme.colors.secondary};
  margin-bottom: 32px;
  font-weight: var(--font-weight-medium);
`;

const StyledContactButton = styled(ContactButton)`
  margin: 0 auto 10px;
`;

const LogoSection = styled.div`
  margin: 0 auto;
  padding-top: 0px;
  width: 100%;
  clear: both;
`;

const StyledLogo = styled(Logo)`
  font-size: 32px;
  margin: 15px auto 24px;
`;

const Address = styled.address`
  font-style: normal;
  margin-bottom: 16px;
  line-height: 1.6;
  font-weight: var(--font-weight-regular);
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 24px;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${({theme}) => theme.colors.darkGray};
  color: ${({theme}) => theme.colors.darkGray};
  transition: all 0.3s;
  
  &:hover {
    background-color: ${({theme}) => theme.colors.darkGray};
    color: white;
  }
`;

const LegalLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  font-size: 12px;
`;

const LegalLink = styled(Link)`
  color: ${({theme}) => theme.colors.darkGray};
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover {
    color: ${({theme}) => theme.colors.secondary};
  }
`;

interface FooterProps {
  className?: string;
  heading?: React.ReactNode;
  subheading?: React.ReactNode;
  buttonText?: string;
  buttonLink?: string;
  customButton?: React.ReactNode;
  pageContext?: string; // Contexte de la page qui utilise le footer
}

const Footer: React.FC<FooterProps> = ({ 
  className,
  heading = (
    <>
      Venez nous voir
      <span role="img" aria-label="waving hand">👋</span>
    </>
  ),
  subheading = "Venez nous rencontrer, ça commence ici",
  buttonText = "Envoyez un message",
  buttonLink = "/contact",
  customButton,
  pageContext = "Site web" // Valeur par défaut si non spécifiée
}) => {
  return (
    <FooterWrapper className={className}>
      <FooterContainer>
        <Heading>
          {heading}
        </Heading>
        
        <Subheading>{subheading}</Subheading>
        
        {customButton ? (
          customButton
        ) : (
          <StyledContactButton context={pageContext}>{buttonText}</StyledContactButton>
        )}
        
        <div>
          <StyledLogo 
            thinkfabColor="#011D3A"
            separatorColor="#011D3A"
            weDoColor="#011D3A"
            devColor="#011D3A"
          />
        </div>
        
        <Address>
          <div>18 avenue Pierre Mendès-France</div>
          <div>67300 Schiltigheim</div>
          <div>+33954570018</div>
        </Address>
        
        <SocialLinks>
          <SocialIcon href="https://www.linkedin.com/company/thinkfab/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
          </SocialIcon>
        </SocialLinks>
        
        <LegalLinks>
          <LegalLink to="/mentions-legales">Mentions légales</LegalLink>
          <LegalLink to="/politique-confidentialite">Politique de confidentialité</LegalLink>
        </LegalLinks>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default Footer;